/**
 * The words an owner reads while recording a sale of their own work — OND-173.
 *
 * Its own file rather than more keys on `SELL_COPY`, for the reason that file
 * gives about itself one screen over: every line here is read moments before
 * somebody moves title to another person, and this act differs from the
 * gallery's in four ways a shared copy block would blur. Which step may be left
 * and how typing becomes minor units is `ownerSale.model.ts`; the draft and the
 * one write are `useOwnerSaleController.ts`.
 *
 * FOUR THINGS THE GALLERY'S WIZARD SAYS THAT THIS ONE MUST NOT, each of them a
 * decision in `20261014093000_an_owner_sells_their_own_work.sql` rather than a
 * field nobody got round to:
 *
 * 1. NO COVENANT (decision 8). `ondo.effect_owner_sale()` has no covenant
 *    argument at all — not a term, a share, a split, an election or a lock. A
 *    covenant's gallery half is collected by the prior sale's effecting firm,
 *    and that column is null on a sale nobody brokered: the instrument would
 *    owe money to nobody and file a settlement no party could read. So there is
 *    no covenant step, no slider, and no sentence saying one could be added
 *    later — OND-89 owns an owner's resale obligations and is a separate
 *    ticket.
 *
 * 2. NO FEE (decisions 6 and 7). "The fee follows the firm": a registration fee
 *    is owed BY THE FIRM THAT EFFECTED THE SALE, and where none did there is no
 *    fee. Andrew recorded the same answer on OND-72 — collectors are never
 *    charged. So there is no fee step, no figure, no total including one and no
 *    line implying one is coming. The copy here never mentions money owed to
 *    ONDO, because a screen that named a fee this door does not raise would be
 *    worse than a screen that mentioned none.
 *
 * 3. NO GALLERY CHANNEL (decision 9). The engine refuses `'gallery'` when no
 *    firm effected the sale, so the channel cannot say a dealer was involved
 *    when the row says none was. `OWNER_SALE_CHANNELS` offers the two words
 *    that are true, rather than offering three and collecting a 422 about a
 *    word the seller never chose.
 *
 * 4. NO BUYER CHECK, AND THE COPY MUST NOT IMPLY ONE. `ondo.transferee_check()`
 *    takes a consignment, so there is nothing to ask on blur of the id field
 *    and `OwnerSaleController`'s header records that as a known gap rather than
 *    inventing a second resolver. `idHelp` therefore says the id is recorded as
 *    typed and checked when the sale is recorded — never "we have confirmed
 *    this is…", which the gallery's wizard can say and this cannot.
 *
 * And two limits that hold for every string in this repository. Nothing here
 * claims a thing is verified, immutable or proven true — see CLAUDE.md's
 * witness rules — and nothing promises a notification, a deadline or a
 * reminder: there is no timer in this product, `'expired'` has no writer, and
 * OND-217 withdrew the last reminder on 13 October.
 */

import type { TransferChannel, VatScheme } from "@ondo/shared";

/**
 * The two channels an owner's own sale can honestly be.
 *
 * NOT `TRANSFER_CHANNELS`, which is three. 20261014093000 decision 9: the
 * engine raises a 22023 on `'gallery'` when no firm effected the sale, so
 * offering the word would be offering a refusal. `'auction'` is accepted and
 * means a sale the owner made at auction, which is a thing the enum can say.
 */
export const OWNER_SALE_CHANNELS = ["private", "auction"] as const satisfies readonly TransferChannel[];

export const OWNER_SALE_COPY = {
  /** The panel on the work's own record, which is where this act is reached from. */
  panel: {
    heading: "Sell this work",
    /**
     * Says what the act is and what it is not, in that order, because the one
     * thing a reader can get wrong here is thinking a press moves title.
     */
    lead: "Record a sale of this work to somebody else. You are the seller; no gallery is involved.",
    open: "Record a sale",
    /**
     * Drawn where the control would be when a gallery holds the work.
     *
     * NOT A GREYED-OUT BUTTON. `ondo.owner_sellable_work_ids()` excludes a work
     * under a live accepted consignment and the engine refuses it with a
     * sentence of its own, so there is nothing to press — and the owner's
     * remedy is a door that exists on this same record, which is what this says
     * rather than leaving the absence to be guessed at.
     */
    consigned:
      "This work is consigned to a gallery, and while that stands the gallery is the party that sells it. Recall the consignment above if you mean to sell it yourself.",
    /** After the write. Says what happened and what has not happened yet. */
    recorded: "The sale is recorded. The work’s record now carries it.",
  },

  dialog: {
    title: "Record a sale of your own work",
    close: "Close",
    back: "← Back",
    next: "Continue →",
    submit: "Record the sale",
    submitting: "Recording the sale…",
  },

  /** The stepper. Order lives in `OWNER_SALE_STEPS`; these are only the words. */
  steps: {
    terms: "Buyer & terms",
    record: "Record",
  },

  /** Used because there is no consignment to read a currency off. */
  defaultCurrency: "GBP",

  // ── 1 · Buyer & terms ────────────────────────────────────────────────────
  buyerLegend: "Buyer / new owner",
  /**
   * The toggle. Same two words as the gallery's, because it is the same
   * question and a second vocabulary for it would be a second rule.
   */
  matchOnNetwork: "Match on network",
  createNewOwner: "Buyer not on ONDO",

  idLabel: "Buyer ONDO id",
  idPlaceholder: "ONDO-…",
  /**
   * THE ONE PLACE THIS SCREEN DIFFERS MOST FROM THE GALLERY'S, and it says so.
   *
   * The consigned wizard asks `ondo.transferee_check()` on blur and makes the
   * director read a name back before Continue lights. That function takes a
   * consignment, so there is nothing to ask here, and this sentence is written
   * to make the refusal legible when it comes rather than to imply it has
   * already been avoided. It does not say "checked", "confirmed" or "verified".
   */
  idHelp:
    "An ONDO id the buyer gave you. Nothing here looks one up, and nothing here checks it: it is recorded exactly as you type it, and the registry answers when you record the sale. If it names nobody this work can be transferred to, you will be told then and nothing will have been recorded.",

  nameLabel: "Buyer name",
  namePlaceholder: "e.g. Michael Hue-Williams",
  nameHelp:
    "A buyer who is not on ONDO. The sale is recorded against the name you write; title stays where it is until they join and claim it.",

  emailLabel: "Buyer email (optional)",
  emailPlaceholder: "name@example.com",
  /**
   * What the address IS, which is not an account and not a promise of one.
   *
   * It is `ondo.transfers.to_email` — the pending reference OND-103's invitation
   * is addressed to and a later claim is matched against. No date is named,
   * because nothing here expires and nothing chases: `buyerClaim` sends ONE
   * invitation and then waits for a confirmation that may never come.
   */
  emailHelp:
    "Where to reach them, if you have it. ONDO writes to that address once so they can claim the work; it is a reference on this sale and not an account, and nothing about the sale depends on an answer.",

  priceLabel: "Sale price",
  currencyLabel: "Currency",
  priceHelp:
    "The agreed figure. Recorded to the penny — a third decimal place is refused rather than rounded away, because rounding a price nobody agreed to is worse than asking again.",

  channelLabel: "How it was sold",
  /** Only the two `OWNER_SALE_CHANNELS` names. `gallery` is not a word this door takes. */
  channel: {
    private: "Privately",
    auction: "At auction",
  } as const satisfies Partial<Record<TransferChannel, string>>,
  channelHelp:
    "A sale with no gallery behind it is not a gallery sale, so those are the two the registry will record.",

  addVat: "Add VAT",
  addVatHelp: "where you charged it; you set the scheme",
  vatSchemeLabel: "VAT scheme",
  vatAmountLabel: "VAT amount",
  vatHelp:
    "Leave the amount empty to record the scheme without a figure. Empty is not zero — “not recorded here” and “none charged” are different facts.",
  vatScheme: {
    none: "None",
    margin: "Margin",
    standard: "Standard",
    zero: "Zero-rated",
  } as const satisfies Record<VatScheme, string>,

  soldOnLabel: "Date of sale (optional)",
  /**
   * Why the field exists at all, which the gallery's wizard does not have.
   *
   * `EffectOwnerSaleRequest.soldAt` "defaults to now; an owner recording an
   * older sale sets it back", and an owner is far likelier than a gallery to be
   * entering something that already happened. Left empty, the key is not sent
   * and `p_sold_at` takes its own `now()` — see `toEffectOwnerSaleRequest`.
   */
  soldOnHelp:
    "Leave this empty for a sale you are recording as it happens. Set it back for one that happened earlier; the date is the day you name, and it cannot be changed afterwards.",

  // ── 2 · Record ───────────────────────────────────────────────────────────
  /**
   * What the press ACTUALLY does, and both halves are the registry's own words
   * about itself rather than the design's "ownership transfers".
   *
   * `ondo.effect_sale_authorised()` writes `'offered'` for a buyer on ONDO and
   * `'unregistered'` for one who is not. Title lands when the buyer accepts,
   * through `ondo.answer_transfer()`, which is the same door a gallery's sale
   * is answered through. Neither sentence names a date or a deadline, because
   * there is none: a buyer who never answers leaves the row standing.
   */
  effect: {
    ondo: "The sale goes on this work’s record now. Title is OFFERED to the buyer and moves when they accept it — until then the work is yours and the record reads “awaiting buyer”. Nothing expires if they do not answer.",
    name: "The sale goes on this work’s record now. The buyer is not on ONDO, so there is nobody to accept: title stays where it is until they join and claim the work. Nothing expires if they never do.",
  },
  /**
   * The one gate on the last step, and it is a STATEMENT rather than a
   * validation.
   *
   * The gallery's version attests to the AUTHORITY a consignment gave it. An
   * owner has no consignment and is attesting to their own title instead, which
   * is the honest form of the design's "I confirm I have full unencumbered
   * title" — and the one case in the product where that sentence is true of the
   * person pressing.
   */
  confirm:
    "I confirm I own this work and am entitled to sell it, and that the terms above are what was agreed.",
  /**
   * WHAT IS NOT PART OF THIS ACT, said on the screen.
   *
   * Both halves would otherwise be read in from the gallery's wizard by anybody
   * who has seen it: a resale covenant, and a fee. Saying so is cheaper than
   * leaving a reader to wonder which step they missed — and the second sentence
   * is the only place in the product where a collector is told they are not
   * being charged, which is a thing worth being told once.
   */
  notPartOfThis:
    "A resale covenant is attached by a gallery that sells a work, because its share is owed to the gallery — there is none on a sale you make yourself. ONDO charges you nothing to record this.",

  summary: {
    work: "Work",
    buyer: "Buyer",
    buyerEmail: "Buyer email",
    price: "Sale price",
    channel: "How it was sold",
    vat: "VAT",
    soldOn: "Date of sale",
    soldOnNow: "Today, as recorded",
  },

  problems: {
    price: "That is not a price the registry can record. Write the figure in the currency named beside it.",
    currency: "A currency is a three-letter code — GBP, USD, EUR.",
    buyerOndoId: "An ONDO id looks like ONDO-8473.",
    buyerName: "Write the buyer’s name, or switch to Match on network and give their ONDO id.",
    buyerEmail: "That is not an email address. Leave it empty if you do not have one.",
    vat: "That is not a VAT figure the registry can record. Leave it empty to record the scheme without one.",
    soldOn: "That is not a date the registry can record.",
  },

  errors: {
    /**
     * Only for the statuses that carry no sentence of their own.
     *
     * `describeOwnerSaleError` prefers the server's own words for every
     * refusal, and this door has several the form cannot predict: a work
     * consigned to a gallery, a work already awaiting title from an earlier
     * sale, a covenant from an earlier sale locking it, an identity standing
     * that is not clear, and the opaque one about the buyer's id. Replacing any
     * of them with a house sentence would tell the seller the wrong thing about
     * their own work.
     */
    refused: "The registry would not record this sale.",
    throttled: "Too many buyer ids have been refused recently. Wait a few minutes and try again.",
    signedOut: "Your session has expired. Sign in again to record this sale.",
  },
} as const;
